import _                                      from 'underscore';
import React, { useEffect, useRef, useState } from 'react';
import { reactLocalStorage }                  from 'reactjs-localstorage';
import { useHistory }                         from 'react-router-dom';
import { History }                            from 'history';

import { EnvironmentObject } from '../environments/types/base';
import { runIfFunction }     from '../util';

//@ts-ignore
let environment: EnvironmentObject = __ENV__;

interface UnsavedDataCheckHookOptions {
	clearIdsOnSuccessfulNav: boolean,
	onNavWithUnsavedData: ( location: History.LocationState ) => void,
}

const hookOptionDefaults: UnsavedDataCheckHookOptions = {
	clearIdsOnSuccessfulNav: true,
	onNavWithUnsavedData:    null,
}

// Retrives storage name from environment
const getStorageKeyName = (): string => {

	return environment.session.cookies.saveDataCheck.namedKey
}

const getStorageValue = () => {

	return reactLocalStorage.getObject( getStorageKeyName(), [] );
}

const setStorageValue = ( value: string[] ) => {

	return reactLocalStorage.setObject( getStorageKeyName(), value );
}

// Hook that manages id array that indicate objects with unsaved data from user
// Discussion on back/forward button: https://github.com/ReactTraining/react-router/issues/5405
export const useUnsavedDataCheck = ( _options: Partial<UnsavedDataCheckHookOptions> = {} ) => {

	// craft hook options
	let options = { ...hookOptionDefaults, ..._options } as UnsavedDataCheckHookOptions;

	// component-localized version updated when storage is updated
	let [ unsavedDataIds, setUnsavedDataIds ] = useState<string[]>( getStorageValue() );

	// get the history object
	const history = useHistory();

	useEffect( () => {

		setStorageValue( unsavedDataIds );

	}, [ unsavedDataIds ] );

	useEffect( () => {

		// Create a subscription and return it's unsubscribe
		return history.listen( ( location ) => {

			if( hasUnsavedData() ){

				// run a function if we have unsaved data present
				runIfFunction( options.onNavWithUnsavedData, location );

				// clear we have unsaved data present
				if( options.clearIdsOnSuccessfulNav ){

					clearUnsavedDataIds();
				}

			}

		} )

	}, [ history ] );

	const addUnsavedDataIds = ( ids: string[] ) => {

		let newIds = _.uniq( _.union( getStorageValue(), ids ) );

		setUnsavedDataIds( newIds );
	}

	const removeUnsavedDataIds = ( ids: string[] ) => {

		let newIds = _.without.apply( null, [ getStorageValue(), ...ids ] )

		setUnsavedDataIds( newIds );
	}

	const clearUnsavedDataIds = () => {

		let newIds: string[] = [];

		setUnsavedDataIds( newIds );
	}

	const hasUnsavedData = () => {

		return ( getStorageValue().length > 0 )
	}

	// Get the freshest data when not actually altering the hook instance
	const getUnsavedData = () => {

		return getStorageValue()
	}

	return Object.assign( {}, {
		addUnsavedDataIds,
		removeUnsavedDataIds,
		clearUnsavedDataIds,
		hasUnsavedData,
		getUnsavedData,
		unsavedDataIds,
        getStorageValue,
        setStorageValue,
	} )
};
